import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';

import {
  apiAdminGetBanners,
  apiAdminCreateBanner,
  apiAdminUpdateBanner,
  apiAdminReorderBanners,
} from 'api';

interface Banner {
  id: number;
  image_url: string | null;
  link_url: string;
  title: string;
  target_roles: number[];
  order: number;
  duration_seconds: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
}

const ROLES = [
  { value: 1, label: 'Admin' },
  { value: 2, label: 'Underwriter' },
  { value: 3, label: 'Broker' },
  { value: 4, label: 'Manager' },
  { value: 5, label: 'Broker Partner' },
  { value: 6, label: 'CSR' },
  { value: 7, label: 'TSR' },
];

const TABLE_HEADERS = [
  'Order',
  'Preview',
  'Title',
  'Duration',
  'Active',
  'Actions',
];

const emptyForm = (): Partial<Banner> & { imageFile?: File } => ({
  title: '',
  link_url: '',
  target_roles: [],
  order: 0,
  duration_seconds: 15,
  is_active: true,
  starts_at: null,
  ends_at: null,
});

const HomepageBannersView: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const lightbox = useDisclosure();
  const fileRef = useRef<HTMLInputElement>(null);

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const openLightbox = (src: string) => {
    setLightboxSrc(src);
    lightbox.onOpen();
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  const [editing, setEditing] = useState<Banner | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [preview, setPreview] = useState<string | null>(null);

  const {
    data: banners = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Banner[]>({
    queryKey: ['admin-banners'],
    queryFn: () =>
      apiAdminGetBanners().then((r: any) =>
        Array.isArray(r) ? r : (r.data ?? []),
      ),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin-banners'] });

  const createMutation = useMutation({
    mutationFn: (fd: FormData) => apiAdminCreateBanner(fd),
    onSuccess: () => {
      toast({ title: 'Banner created', status: 'success' });
      invalidate();
      onClose();
    },
    onError: () => toast({ title: 'Failed to create banner', status: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, fd }: { id: number; fd: FormData }) =>
      apiAdminUpdateBanner(id, fd),
    onSuccess: () => {
      toast({ title: 'Banner updated', status: 'success' });
      invalidate();
      onClose();
    },
    onError: () => toast({ title: 'Failed to update banner', status: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => {
      const fd = new FormData();
      fd.append('is_active', String(is_active));
      return apiAdminUpdateBanner(id, fd);
    },
    onSuccess: invalidate,
    onError: (err: any) => {
      // PATCH utility rejects with error.response.data directly
      const msg =
        err?.is_active?.[0] ||
        err?.detail ||
        err?.non_field_errors?.[0] ||
        'Failed to update banner';
      toast({ title: msg, status: 'error' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (items: { id: number; order: number }[]) =>
      apiAdminReorderBanners(items),
    onSuccess: invalidate,
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setPreview(null);
    onOpen();
  };

  const openEdit = (b: Banner) => {
    setEditing(b);
    setForm({ ...b, imageFile: undefined });
    setPreview(b.image_url);
    onOpen();
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, imageFile: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    const fd = new FormData();
    if (form.imageFile) fd.append('image', form.imageFile);
    fd.append('title', form.title ?? '');
    fd.append('link_url', form.link_url ?? '');
    fd.append('order', String(form.order ?? 0));
    fd.append('duration_seconds', String(form.duration_seconds ?? 15));
    fd.append('is_active', String(form.is_active ?? true));
    fd.append('target_roles', JSON.stringify(form.target_roles ?? []));
    if (form.starts_at) fd.append('starts_at', form.starts_at);
    if (form.ends_at) fd.append('ends_at', form.ends_at);

    if (editing) {
      updateMutation.mutate({ id: editing.id, fd });
    } else {
      createMutation.mutate(fd);
    }
  };

  const handleReorder = (id: number, direction: 'up' | 'down') => {
    const sorted = [...banners].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((b) => b.id === id);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sorted.length - 1) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    const reordered = sorted.map((b, i) => {
      if (i === idx) return { id: b.id, order: sorted[swap].order };
      if (i === swap) return { id: b.id, order: sorted[idx].order };
      return { id: b.id, order: b.order };
    });
    reorderMutation.mutate(reordered);
  };

  const isBusy = createMutation.isPending || updateMutation.isPending;

  const sorted = [...banners].sort((a, b) => a.order - b.order);

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Homepage Banners
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh banners" hasArrow>
            <IconButton
              aria-label="Refresh banners"
              icon={<RepeatIcon />}
              onClick={() => refetch()}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={openCreate}
          >
            New Banner
          </Button>
        </Flex>
      </Flex>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading banners...
                </Flex>
              </Td>
            </Tr>
          )}

          {sorted.map((b, idx) => (
            <Tr
              key={b.id}
              cursor="pointer"
              opacity={b.is_active ? 1 : 0.5}
              _hover={{ bg: hoverBg }}
              onClick={() => openEdit(b)}
            >
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Move up"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronUpIcon boxSize={4} />}
                    isDisabled={idx === 0}
                    onClick={() => handleReorder(b.id, 'up')}
                  />
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {b.order}
                  </Text>
                  <IconButton
                    aria-label="Move down"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronDownIcon boxSize={4} />}
                    isDisabled={idx === sorted.length - 1}
                    onClick={() => handleReorder(b.id, 'down')}
                  />
                </HStack>
              </Td>
              <Td>
                {b.image_url ? (
                  <Tooltip
                    label="Click to view full size"
                    hasArrow
                    openDelay={400}
                  >
                    <Box
                      display="inline-block"
                      borderRadius="md"
                      overflow="hidden"
                      cursor="zoom-in"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLightbox(b.image_url as string);
                      }}
                    >
                      <img
                        src={b.image_url}
                        alt={b.title || 'Banner preview'}
                        style={{
                          height: '28px',
                          width: 'auto',
                          maxWidth: '160px',
                          objectFit: 'contain',
                          display: 'block',
                          borderRadius: '6px',
                        }}
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <Box
                    w="120px"
                    h="28px"
                    bg={hoverBg}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="10px" color={mutedColor}>
                      No image
                    </Text>
                  </Box>
                )}
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm" fontWeight="700">
                  {b.title || '—'}
                </Text>
                {b.link_url && (
                  <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                    {b.link_url}
                  </Text>
                )}
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {b.duration_seconds}s
                </Text>
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <Switch
                  size="sm"
                  isChecked={b.is_active}
                  onChange={(e) =>
                    toggleMutation.mutate({
                      id: b.id,
                      is_active: e.target.checked,
                    })
                  }
                />
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <Button size="sm" variant="outline" onClick={() => openEdit(b)}>
                  Edit
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!isLoading && sorted.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px" color={mutedColor}>
          No banners yet. Click "New Banner" to create one.
        </Box>
      )}

      {/* ── Image Lightbox ── */}
      <Modal
        isOpen={lightbox.isOpen}
        onClose={lightbox.onClose}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton
            color="white"
            bg="blackAlpha.600"
            borderRadius="full"
            _hover={{ bg: 'blackAlpha.800' }}
          />
          {lightboxSrc && (
            <Image
              src={lightboxSrc}
              alt="Banner full size"
              w="100%"
              maxH="80vh"
              objectFit="contain"
              borderRadius="md"
            />
          )}
        </ModalContent>
      </Modal>

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? 'Edit Banner' : 'Add Banner'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Image upload */}
              <FormControl>
                <FormLabel fontSize="sm">Banner Image (JPG/JPEG)</FormLabel>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".jpg,.jpeg"
                  style={{ display: 'none' }}
                  onChange={handleFile}
                />
                <Box
                  border="2px dashed"
                  borderColor={preview ? 'orange.400' : borderColor}
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                  cursor="pointer"
                  _hover={{ borderColor: 'orange.400' }}
                  onClick={() => fileRef.current?.click()}
                  position="relative"
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {preview ? (
                    <Image
                      src={preview}
                      maxH="120px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  ) : (
                    <VStack spacing={1}>
                      <Text fontSize="sm" color={mutedColor}>
                        Click to upload image
                      </Text>
                      <Text fontSize="xs" color={mutedColor}>
                        JPG / JPEG
                      </Text>
                    </VStack>
                  )}
                </Box>
                {preview && (
                  <Button
                    size="xs"
                    mt={1}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      setPreview(null);
                      setForm((f) => ({ ...f, imageFile: undefined }));
                    }}
                  >
                    Remove image
                  </Button>
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Title</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.title ?? ''}
                  placeholder="Banner title"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Link URL (click-through)</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.link_url ?? ''}
                  placeholder="https://..."
                  onChange={(e) =>
                    setForm((f) => ({ ...f, link_url: e.target.value }))
                  }
                />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Order</FormLabel>
                  <NumberInput
                    size="md"
                    min={0}
                    value={form.order ?? 0}
                    onChange={(_, v) => setForm((f) => ({ ...f, order: v }))}
                  >
                    <NumberInputField bg={inputBg} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Duration (seconds)</FormLabel>
                  <NumberInput
                    size="md"
                    min={3}
                    max={60}
                    value={form.duration_seconds ?? 15}
                    onChange={(_, v) =>
                      setForm((f) => ({ ...f, duration_seconds: v }))
                    }
                  >
                    <NumberInputField bg={inputBg} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">
                  Target roles (empty = all roles)
                </FormLabel>
                <Select
                  variant="main"
                  size="md"
                  bg={inputBg}
                  placeholder="— All roles —"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (!v) return setForm((f) => ({ ...f, target_roles: [] }));
                    setForm((f) => ({
                      ...f,
                      target_roles: f.target_roles?.includes(v)
                        ? f.target_roles.filter((r) => r !== v)
                        : [...(f.target_roles ?? []), v],
                    }));
                  }}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
                {(form.target_roles?.length ?? 0) > 0 && (
                  <HStack mt={2} flexWrap="wrap" spacing={1}>
                    {form.target_roles?.map((rid) => (
                      <Badge
                        key={rid}
                        colorScheme="orange"
                        cursor="pointer"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            target_roles: f.target_roles?.filter(
                              (r) => r !== rid,
                            ),
                          }))
                        }
                      >
                        {ROLES.find((r) => r.value === rid)?.label} ✕
                      </Badge>
                    ))}
                  </HStack>
                )}
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Start date (optional)</FormLabel>
                  <Input
                    type="datetime-local"
                    variant="main"
                    size="md"
                    bg={inputBg}
                    value={form.starts_at?.slice(0, 16) ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        starts_at: e.target.value || null,
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">End date (optional)</FormLabel>
                  <Input
                    type="datetime-local"
                    variant="main"
                    size="md"
                    bg={inputBg}
                    value={form.ends_at?.slice(0, 16) ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        ends_at: e.target.value || null,
                      }))
                    }
                  />
                </FormControl>
              </HStack>

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel fontSize="sm" mb={0}>
                  Active
                </FormLabel>
                <Switch
                  isChecked={form.is_active ?? true}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, is_active: e.target.checked }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={isBusy} onClick={handleSubmit}>
              {editing ? 'Save changes' : 'Create banner'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default HomepageBannersView;
